<template>
  <TolgeeProvider>
    <div class="relative flex flex-col items-center justify-center min-h-screen font-sans overflow-hidden">
      <!-- Background layers matching default layout -->
      <div class="absolute inset-0 bg-blue-fade -z-[1] pointer-events-none" />
      <div class="absolute inset-0 bg-gray-100 -z-[2] pointer-events-none" />

      <div class="flex flex-col items-center max-w-lg px-6 text-center">
        <!-- Logo -->
        <img
          alt="Statycs Logo"
          src="@/assets/images/mainLogo.svg"
          class="w-10 h-10 mb-8"
        />

        <!-- Error code -->
        <h1 class="text-7xl font-bold text-blue-500 tracking-tight leading-none mb-4">
          {{ error?.statusCode || 500 }}
        </h1>

        <!-- Friendly title -->
        <h2 class="text-2xl font-semibold text-coldgray-900 mb-2">
          {{ title }}
        </h2>

        <!-- Description -->
        <p class="text-base text-coldgray-600 mb-8 leading-relaxed">
          {{ description }}
        </p>
        <!-- Actions -->
        <div class="flex items-center gap-3">
          <UButton
            size="xl"
            color="primary"
            @click="handleClearError"
          >
            {{ t('error-page.go-to-homepage') }}
          </UButton>

          <UButton
            size="xl"
            color="white"
            variant="solid"
            @click="retry"
          >
            {{ t('error-page.try-again') }}
          </UButton>
        </div>
      </div>

      <!-- Footer -->
      <p class="absolute bottom-6 text-xs text-coldgray-400">
        &copy; {{ currentYear }} Statycs. {{ t('error-page.all-rights-reserved') }}
      </p>
    </div>
  </TolgeeProvider>
</template>

<script lang="ts" setup>
import type { NuxtError } from '#app';
import { TolgeeProvider } from '@tolgee/vue';
import { useTranslate } from '~/composables/useTranslate';

const { t } = useTranslate();

const props = defineProps<{
  error: NuxtError;
}>();

const currentYear = new Date().getFullYear();

const title = computed(() => {
  const code = props.error?.statusCode;
  switch (code) {
    case 400:
      return t('error-page.title.400');
    case 401:
      return t('error-page.title.401');
    case 403:
      return t('error-page.title.403');
    case 404:
      return t('error-page.title.404');
    case 408:
      return t('error-page.title.408');
    case 429:
      return t('error-page.title.429');
    case 500:
      return t('error-page.title.500');
    case 502:
      return t('error-page.title.502');
    case 503:
      return t('error-page.title.503');
    default:
      return t('error-page.title.default');
  }
});

const description = computed(() => {
  const code = props.error?.statusCode;
  switch (code) {
    case 404:
      return t('error-page.description.404');
    case 403:
      return t('error-page.description.403');
    case 401:
      return t('error-page.description.401');
    case 500:
      return t('error-page.description.500');
    case 503:
      return t('error-page.description.503');
    default:
      return t('error-page.description.default');
  }
});

const handleClearError = () => {
  clearError({ redirect: '/' });
};

const retry = () => {
  clearError();
  // Reload the current page
  if (import.meta.client) {
    window.location.reload();
  }
};
</script>
